import React, { useEffect, useState } from "react";
import { MessageCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "../ui/switch";
import { CONTACT } from "../../lib/constants";
import { authApi, errorMessage } from "../../lib/api";

export const waLink = (text) => `https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent(text || "Hi KohimaTaxi, I need help with ")}`;

export function WhatsAppChatButton({ text, label = "Chat with us on WhatsApp", className = "", variant = "solid", testId = "whatsapp-chat-button" }) {
  const base = variant === "solid"
    ? "bg-[#25D366] text-white hover:bg-[#1ebe5d]"
    : variant === "light" ? "bg-white/10 text-white ring-1 ring-white/20 hover:bg-white/20" : "bg-brand-50 text-brand-700 ring-1 ring-brand-200 hover:bg-brand-100";
  return (
    <a href={waLink(text)} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${base} ${className}`} data-testid={testId}>
      <MessageCircle className="h-4 w-4" /> {label}
    </a>
  );
}

const OPT_IN_KEY = "kt_whatsapp_opt_in";  // legacy local key (kept for migration)
void OPT_IN_KEY;

// Preference lives on the backend as `whatsapp_opt_in` once PATCH /api/me exists; until then it is kept locally.
export function WhatsAppOptIn({ user, onSaved }) {
  const backendField = true;  // PATCH /auth/me now persists whatsapp_opt_in
  const [on, setOn] = useState(() => (user?.whatsapp_opt_in === true));
  const [busy, setBusy] = useState(false);
  useEffect(() => { setOn(user?.whatsapp_opt_in === true); }, [user?.whatsapp_opt_in]);

  const toggle = async (v) => {
    setOn(v);
    setBusy(true);
    try { const u = await authApi.updateProfile({ whatsapp_opt_in: v }); onSaved?.(u.user || u); toast.success(v ? "WhatsApp updates on" : "WhatsApp updates off"); }
    catch (e) { setOn(!v); toast.error(errorMessage(e)); } finally { setBusy(false); }
  };

  return (
    <div className="flex items-start justify-between gap-4" data-testid="whatsapp-opt-in">
      <div>
        <p className="text-sm font-semibold text-ink">WhatsApp updates</p>
        <p className="text-xs text-ink-soft">Ride confirmations, driver on the way, order status and receipts on WhatsApp{user?.phone ? ` (${user.phone})` : " — add a phone number first"}.</p>
      </div>
      <div className="flex items-center gap-2">{busy && <Loader2 className="h-4 w-4 animate-spin text-ink-muted" />}<Switch checked={on} onCheckedChange={toggle} disabled={busy || !user?.phone} data-testid="whatsapp-opt-in-switch" /></div>
    </div>
  );
}

export function SocialLinks({ light = false, className = "", testId = "social-links" }) {
  const cls = light ? "bg-white/10 text-white hover:bg-white/20" : "bg-surface-100 text-ink-soft hover:bg-brand-100 hover:text-brand-700";
  return (
    <div className={`flex items-center gap-2 ${className}`} data-testid={testId}>
      <a href={CONTACT.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" title="Instagram @kohimataxi_delivery" className={`flex h-9 w-9 items-center justify-center rounded-full transition-colors ${cls}`} data-testid={`${testId}-instagram`}>
        <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" /><circle cx="12" cy="12" r="4" /><circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" /></svg>
      </a>
      <a href={CONTACT.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" title="Facebook: Kohima Taxi & Food Delivery" className={`flex h-9 w-9 items-center justify-center rounded-full transition-colors ${cls}`} data-testid={`${testId}-facebook`}>
        <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" fill="currentColor"><path d="M13.5 21v-7h2.4l.4-3h-2.8V9.1c0-.9.3-1.5 1.5-1.5h1.4V4.9c-.3 0-1.2-.1-2.2-.1-2.2 0-3.7 1.3-3.7 3.8V11H8v3h2.5v7h3z" /></svg>
      </a>
      <a href={waLink()} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" title={`WhatsApp ${CONTACT.phone}`} className={`flex h-9 w-9 items-center justify-center rounded-full transition-colors ${cls}`} data-testid={`${testId}-whatsapp`}>
        <MessageCircle className="h-[18px] w-[18px]" />
      </a>
    </div>
  );
}
